import { useMemo } from 'react';
import { applySearch, applyColumnFilters, applySort, hasColumnFilters, resolveSelection } from '../utils/listSearch';

/**
 * The one dataset a list/datasheet page shows: sort -> search -> column filters -> page.
 *
 * Every view of the page (list, datasheet, count chip, export, detail pane) reads from what this
 * returns, so they cannot disagree about which rows exist or in what order. The grid is
 * presentational: hand it `filtered` (or `paged`) as data, `searched` as allData, and the sort as
 * a controlled prop, so the sort lives here and survives a list/datasheet toggle.
 *
 * Sort runs FIRST, over the whole dataset, and search and filters keep that order. So a search
 * never re-sorts, and sorting happens before paging (header sort covers every page, not the one
 * on screen). `sortConfig` wins; `defaultSort` applies until the user picks one.
 *
 * Returns the same row objects it was given (listSearch caches per-row search text on them), so
 * callers should keep `rows`, `searchFields` and `columns` referentially stable.
 */
export const useListTrunk = ({
  rows,
  query = '',
  searchFields = [],
  labels = [],
  filters = {},
  sortConfig = null,
  defaultSort = null,
  columns = [],
  page = 1,
  pageSize = 0,
  picked = null,
  selectionKey = 'id',
  keep = false,
}) => {
  const activeSort = sortConfig?.key && sortConfig.direction ? sortConfig : defaultSort;

  const sorted = useMemo(
    () => applySort(rows || [], activeSort, columns),
    // activeSort is compared by value; a fresh defaultSort object each render must not re-sort
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, activeSort?.key, activeSort?.direction, columns]
  );
  const searched = useMemo(
    () => applySearch(sorted, query, searchFields, labels),
    [sorted, query, searchFields, labels]
  );
  const filtered = useMemo(() => applyColumnFilters(searched, filters), [searched, filters]);

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const paged = useMemo(
    () => (pageSize > 0 ? filtered.slice((page - 1) * pageSize, page * pageSize) : filtered),
    [filtered, page, pageSize]
  );

  const selected = useMemo(
    () => resolveSelection(filtered, picked, { key: selectionKey, keep }),
    [filtered, picked, selectionKey, keep]
  );

  return {
    searched,
    filtered,
    paged,
    totalPages,
    selected,
    hasSearch: !!(query && query.trim()),
    hasFilters: hasColumnFilters(filters),
  };
};

export default useListTrunk;
